import { useQuery } from "@tanstack/react-query";

import { privateHttp } from "~/config/axios";
import { API_ENDPOINTS } from "~/config/endpoints";
import type {
  ContractHistoryListRequest,
  ContractHistoryListResponse,
} from "~/types/common";

/**
 * 레거시(HIST_CONTRACT) + 신규(HIST_CONTRACT_REQUEST) 통합 계약 내역 API.
 *
 * - 사장님 화면 → useContractHistoryForCompany({ companySeq })
 * - 보호자 화면 → useContractHistoryForUser()
 *
 * 응답 행의 `source` 필드로 LEGACY/REQUEST 를 구분하고, 상세 진입 시 분기.
 */

const CONTRACT_HISTORY_QUERY_KEYS = {
  company: ["contract-history", "company"] as const,
  user: ["contract-history", "user"] as const,
};

const getContractHistoryForCompany = async (
  params: Required<Pick<ContractHistoryListRequest, "companySeq">> &
    Omit<ContractHistoryListRequest, "companySeq">,
): Promise<ContractHistoryListResponse> => {
  const { data } = await privateHttp.get<ContractHistoryListResponse>(
    API_ENDPOINTS.CONTRACT_HISTORY.COMPANY,
    {
      params: {
        companySeq: params.companySeq,
        page: params.page ?? 1,
        size: params.size ?? 20,
      },
    },
  );
  return data;
};

const getContractHistoryForUser = async (
  params: Omit<ContractHistoryListRequest, "companySeq">,
): Promise<ContractHistoryListResponse> => {
  const { data } = await privateHttp.get<ContractHistoryListResponse>(
    API_ENDPOINTS.CONTRACT_HISTORY.USER,
    {
      params: {
        page: params.page ?? 1,
        size: params.size ?? 20,
      },
    },
  );
  return data;
};

export const useContractHistoryForCompany = (
  params: Required<Pick<ContractHistoryListRequest, "companySeq">> &
    Omit<ContractHistoryListRequest, "companySeq">,
) =>
  useQuery({
    queryKey: [...CONTRACT_HISTORY_QUERY_KEYS.company, params],
    queryFn: () => getContractHistoryForCompany(params),
    enabled: !!params.companySeq && params.companySeq > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

export const useContractHistoryForUser = (
  params: Omit<ContractHistoryListRequest, "companySeq"> = {},
) =>
  useQuery({
    queryKey: [...CONTRACT_HISTORY_QUERY_KEYS.user, params],
    queryFn: () => getContractHistoryForUser(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
